import { AdyenCheckout, ApplePay, GooglePay, PayByBank, CustomCard, OnChangeData, SubmitData, PaymentData, CoreConfiguration, AdditionalDetailsData, ValidationError, CardFieldValidData, CardFocusData, CardConfigSuccessData, UIElement } from '@adyen/adyen-web';
import { PayPal, PayPalCaller } from './paypal';
import { IMarketingRequest, IPayment, PreferenceAnswer } from '../../Unbundled/ICheckoutModels';
import { I3ds2Response, IInitiatePaymentResponse } from '../../Unbundled/IComponentState';

type AdyenEnvironment = "test" | "live";

/** A callback signature used in the gift card field validation chain. */
type GiftCardClientValidationCallback = (isValid: boolean) => void;

interface IGiftCardValidation {
    additionalPaymentRequired: boolean;
    amountFromCardNumeric: number;
    amountOnCard: string;
    amountFromCard: string;
    balanceToPay: string;
    delivery: string;
    errorMessage: string;
    subTotal: string;
    totalInUnits: number;
}

class BasketAmounts {
    public currencyIsoCode: string;
    public total: number;
    public subTotal: string;
    public deliveryTotal: string;
    public totalAsDecimal: string;

    constructor(currencyIsoCode: string, total: number, subTotal: string, deliveryTotal: string, totalAsDecimal: string) {
        this.currencyIsoCode = currencyIsoCode;
        this.total = total;
        this.subTotal = subTotal;
        this.deliveryTotal = deliveryTotal;
        this.totalAsDecimal = totalAsDecimal;
    }
}

export class Payment {
    public static async InitAsync() {
        window.paymentPage = new Payment();
        await window.paymentPage.InitialiseAsync();
    }

    private addressContainer = document.getElementById("AddressEditable") as HTMLUListElement;
    private applePayComponentData: PaymentData;
    private cardComponent: CustomCard;
    private checkout: any;
    private googlePayComponentData: PaymentData;
    private payByBankData: any;
    private cardholderNameElement = <HTMLInputElement>document.getElementById("CardHolderName");
    private cvcHidden: boolean = false;
    private readonly formId = "payment-form";
    private readonly formSelector = "#" + this.formId;
    private form = document.getElementById(this.formId) as HTMLFormElement;
    private readonly giftCardAddedElement = document.getElementById("GiftCardAdded") as HTMLInputElement;
    private giftCardNumberElement = document.getElementById("GiftCardNumber") as HTMLInputElement;
    private giftCardVerificationElement = document.getElementById("GiftCardVerificationCode") as HTMLInputElement;
    private idempotencyKey: string;
    private localeUrlPrefix = document.body.dataset.localeUrlPrefix;
    private securedFieldsDiv = document.querySelector<HTMLDivElement>('.secured-fields');
    private enableValidationForCardholderNameField: boolean = false;
    private enableValidationForMainCardFields: boolean = false; // Only enabled when submitting card details, otherwise a recursive event loop is generated.
    //private turnstileWrapper = new TurnstileWrapper();
    private validateGiftCard = document.getElementById("ValidateGiftCard") as HTMLButtonElement;
    private verificationComponent: UIElement;

    private paymentTypeApplePaySelected = <HTMLInputElement>document.getElementById("PaymentTypeApplePaySelected");
    private paymentTypeCardSelected = <HTMLInputElement>document.getElementById("PaymentTypeCardSelected");
    private paymentTypeGiftCardSelected = <HTMLInputElement>document.getElementById("PaymentTypeGiftCardSelected");
    private paymentTypeGooglePaySelected = <HTMLInputElement>document.getElementById("PaymentTypeGooglePaySelected");
    private paymentTypePayByBankSelected = <HTMLInputElement>document.getElementById("PaymentTypePayByBankSelected");
    private paymentTypePayPalSelected = <HTMLInputElement>document.getElementById("PaymentTypePayPalSelected");
    private removeGiftCardLink = <HTMLAnchorElement>document.getElementById("remove-gift-card");

    private focusCss = "pm-input-field--focus";
    private validFieldCss = "adyen-input-valid";
    private verificationContainerId = "verification-container";

    public async InitialiseAsync() {
        // Some browsers cache field inputs (checkboxes) which control the payment type containers.
        // This resets them all to default (only card visible) even if the page is served from cache.
        this.resetPaymentTypeCheckboxes();
        this.SetPaymentBlocksVisiblity();
        this.wireUpPaymentBlockHiding();
        this.WireUpGiftCardHandling();
        this.WireUpSameAsDeliveryButtons();
        this.wireUpCardPaymentEvents();
        this.wireUpCountryChange();

        this.idempotencyKey = this.readFieldValue("IdempotencyKey");
        let adyenConfiguration = this.loadAdyenConfiguration();
        this.checkout = await AdyenCheckout(adyenConfiguration);

        this.catchSubmitOnPaymentForm();

        this.cardComponent = this.configureCreditCards(this.checkout);
        this.resetApplePay(this.checkout);
        this.configureGooglePay(this.checkout);
        // Commented out until ready to go live with Pay By Bank.
        //this.configurePayByBank(this.checkout);

        PayPal.Init((data, action) => this.onPayWithPayPalClicked(data, action), PayPalCaller.PaymentPage);

        this.MapLoqateFields();
    }

    private resetPaymentTypeCheckboxes(): void {
        this.setCheckbox(this.paymentTypeApplePaySelected, false);
        this.setCheckbox(this.paymentTypeCardSelected, true);
        this.setCheckbox(this.paymentTypeGiftCardSelected, false);
        this.setCheckbox(this.paymentTypeGooglePaySelected, false);
        this.setCheckbox(this.paymentTypePayByBankSelected, false);
        this.setCheckbox(this.paymentTypePayPalSelected, false);
    }

    private setCheckbox(checkbox: HTMLInputElement, checked: boolean) {
        if (checkbox) {
            checkbox.checked = checked;
        }
    }

    private onPayWithPayPalClicked(data, actions): Promise<any> {
        if (window.validationService.isValid(this.form)) {
            return actions.resolve();
        }

        // Scroll to the top so the failure can be seen, otherwise it might stay out of sight on thin displays.
        window.scrollTo(0, 0);

        return actions.reject();
    }

    /**
     * Validate the form before Apple Pay / Google Pay is shown.
     * Checks the billing address etc.
     * @param resolve
     * @param reject
     * @returns
     */
    private onPayWithThirdPartyClicked(resolve, reject): void {
        if (window.validationService.isValid(this.form)) {
            return resolve();
        }

        // Scroll to the top so the failure can be seen, otherwise it might stay out of sight on thin displays.
        window.scrollTo(0, 0);

        return reject();
    }

    private WireUpSameAsDeliveryButtons(): void {
        let radios = document.querySelectorAll<HTMLInputElement>('.address-radio');

        for (const element of radios) {
            element.addEventListener("change", (e) => this.CheckAddressVisibility());
        }
    }

    public HideAddressEntry(container: HTMLUListElement): void {
        this.hideElements(container, ".address-entry");
        this.showElements(container, ".enter-address");

        // As well as hiding the fields, disabling them turns off validation.
        this.disableElements(container, "li.address-entry > input[type=text]");

        let editorVisible = document.querySelector(".editor-visible") as HTMLInputElement;
        editorVisible.value = "false";
    }

    public ShowAddressEntry(container: HTMLUListElement): void {
        this.showElements(container, ".address-entry");
        this.hideElements(container, ".enter-address");

        // Enabling the field will also enable the validation for the input.
        this.enableElements(container, "li.address-entry > input[type=text]");

        let editorVisible = document.querySelector<HTMLInputElement>(".editor-visible");
        editorVisible.value = "true";
    }

    // TODO: A good central helper util.
    private processElements(container: HTMLElement, selector: string, action: (element: HTMLInputElement) => void): void {
        let elements = container.querySelectorAll(selector) as NodeListOf<HTMLInputElement>;

        for (const element of elements) {
            action(element);
        }
    }

    private disableElements(container: HTMLElement, selector: string): void {
        this.processElements(container, selector, (element) => element.disabled = true);
    }

    private enableElements(container: HTMLElement, selector: string): void {
        this.processElements(container, selector, (element) => element.disabled = false);
    }

    private hideElements(container: HTMLElement, selector: string): void {
        this.processElements(container, selector, (element) => element.classList.add("hidden"));
    }

    private showElements(container: HTMLElement, selector: string): void {
        this.processElements(container, selector, (element) => element.classList.remove("hidden"));
    }

    public IsAddressEditorVisible(): boolean {
        return document.querySelector<HTMLInputElement>(".editor-visible")
            .value === true.toString();
    }

    private ShowCheckoutBillingAddressEntry(): void {
        this.ShowAddressEntry(this.addressContainer);
        document.querySelector(".address-editable").classList.remove("hidden");
        document.querySelector(".address-readonly").classList.add("hidden");
    }

    private HideCheckoutBillingAddressEntry(): void {
        this.HideAddressEntry(this.addressContainer);

        document.querySelector(".address-editable").classList.add("hidden");
        document.querySelector(".address-readonly").classList.remove("hidden");
    }

    private BillingAddressIsBlank(): boolean {
        // Only reflects the state of the billing address at load (intentional).
        let element = <HTMLInputElement>document.getElementById("BillingAddressIsBlank");
        return element.value.toLowerCase() == "true";
    }

    private IsDeliverToStoreOrder(): boolean {
        let element = <HTMLInputElement>document.getElementById("IsDeliverToStoreOrder");
        return element.value.toLowerCase() == "true";
    }

    private isBillingSameAsDelivery(): boolean {
        let yesRadio = document.getElementById("IsBillingAddressSameAsDelivery_0") as HTMLInputElement;

        if (!yesRadio) {
            // No radio buttons found which means the UI for billing address entry is forced on (required). This would be a deliver to store order.
            return false;
        }

        if (yesRadio.checked) {
            return true;
        }

        return false;
    }

    private CheckAddressVisibility(): void {
        if (this.IsDeliverToStoreOrder() && this.BillingAddressIsBlank()) {
            this.ShowCheckoutBillingAddressEntry();
        }
        else if (this.isBillingSameAsDelivery()) {
            this.HideCheckoutBillingAddressEntry();
        }
        else {
            this.ShowCheckoutBillingAddressEntry();
        }
    }

    private wireUpCardPaymentEvents(): void {
        this.cardholderNameElement.onblur = () => this.enableValidationForCardholderNameField = true;
        this.cardholderNameElement.oncut = (ev) => this.delayedCardholderCheck(ev);
        this.cardholderNameElement.onfocus = () => this.enableValidationForCardholderNameField = true;
        this.cardholderNameElement.onkeyup = (ev) => this.onCardholderNameChanged(ev);
        this.cardholderNameElement.onpaste = (ev) => this.delayedCardholderCheck(ev);
    }

    private wireUpPaymentBlockHiding(): void {
        let paymentTypes = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName("pay-option");

        for (const element of paymentTypes) {
            element.onchange = (e: Event) => this.OnPaymentTypeChange(e);
        }
    }

    private wireUpCountryChange(): void {
        let countryElement = <HTMLSelectElement>document.getElementById("Address_CountryCodeIso3");
        countryElement.addEventListener('change', (e) => this.onCountryChanged());
    }

    private OnPaymentTypeChange(e: Event): void {
        let element = <HTMLInputElement>e.target;

        // Only Gift card can be selected with another payment method. Other payment types cannot be mixed on the same order.
        if (element.id === this.paymentTypeApplePaySelected.id) {
            this.paymentTypeCardSelected.checked = false;
            this.paymentTypeGooglePaySelected.checked = false;
            this.paymentTypePayByBankSelected.checked = false;
            this.paymentTypePayPalSelected.checked = false;
        }
        else if (element.id === this.paymentTypeCardSelected.id) {
            this.paymentTypeApplePaySelected.checked = false;
            this.paymentTypeGooglePaySelected.checked = false;
            this.paymentTypePayByBankSelected.checked = false;
            this.paymentTypePayPalSelected.checked = false;
        }
        else if (element.id === this.paymentTypeGooglePaySelected.id) {
            this.paymentTypeApplePaySelected.checked = false;
            this.paymentTypeCardSelected.checked = false;
            this.paymentTypePayByBankSelected.checked = false;
            this.paymentTypePayPalSelected.checked = false;
        }
        else if (element.id === this.paymentTypePayByBankSelected.id) {
            this.paymentTypeApplePaySelected.checked = false;
            this.paymentTypeCardSelected.checked = false;
            this.paymentTypeGooglePaySelected.checked = false;
            this.paymentTypePayPalSelected.checked = false;
        }
        else if (element.id === this.paymentTypePayPalSelected.id) {
            this.paymentTypeApplePaySelected.checked = false;
            this.paymentTypeCardSelected.checked = false;
            this.paymentTypePayByBankSelected.checked = false;
            this.paymentTypeGooglePaySelected.checked = false;
        }

        this.SetPaymentBlocksVisiblity();
    }

    ///**
    // * Resets the card component UI to its initial state, clears fields and validation.
    // */
    //private resetCardComponentUi() {
    //    this.cardComponent.unmount();
    //    this.cardComponent = this.configureCreditCards(this.checkout);

    //    let cardNumberField = document.querySelector(".card-number");

    //    let img = document.createElement("img");
    //    img.setAttribute("class", "pm-image");
    //    img.setAttribute("id", "pmImage");
    //    img.setAttribute("width", "40");
    //    img.setAttribute("src", "https://checkoutshopper-live.adyen.com/checkoutshopper/images/logos/nocard.svg");

    //    cardNumberField.insertBefore(img, cardNumberField.firstChild);

    //    // Reset any validation CSS around the secured fields as they are now empty.
    //    this.onFieldValid({ rootNode: this.securedFieldsDiv, fieldType: "encryptedCardNumber" });
    //    this.onFieldValid({ rootNode: this.securedFieldsDiv, fieldType: "encryptedExpiryDate" });
    //    this.onFieldValid({ rootNode: this.securedFieldsDiv, fieldType: "encryptedSecurityCode" });
    //}

    private WireUpGiftCardHandling(): void {
        if (!this.removeGiftCardLink) {
            return;
        }
        this.removeGiftCardLink.onclick = (e) => {
            e.preventDefault();
            this.removeGiftCard();
        };
        this.validateGiftCard.addEventListener("click", (e) => this.onValidateGiftCardRequested(e));
    }

    private removeGiftCard(): void {
        this.setGiftCardBalanceVisibility(false);
        this.setGiftCardFieldState(false);
        this.giftCardAddedElement.value = "";
        this.giftCardNumberElement.value = "";
        this.giftCardVerificationElement.value = "";
        document.querySelector(".c-gift-card-amount").textContent = "";
        this.processElements(document.body, ".c-gift-card-balance", (element) => element.textContent = "");
        this.resetApplePay(this.checkout);
        this.validateGiftCard.classList.remove("hidden");
    }

    /**
     * Runs when client validation of the gift cards fields is complete.
     * This is only a simple check to ensure the fields are valid.
     */
    private onGiftCardClientValidationComplete(areGiftCardFieldsValid: boolean): void {

        if (!areGiftCardFieldsValid) {
            return;
        }

        let data = {
            giftCardNumber: this.giftCardNumberElement.value,
            giftCardVerification: this.giftCardVerificationElement.value,
        };

        // Clear any gift card validation messages while we wait for the server to respond.
        this.setGiftCardValidationUi(true, "");
        // Disable the inputs and only re-enable them if the server validation fails or gift card is reset.
        this.setGiftCardFieldState(true);
        window.FormSubmitToggle.DisableForm(this.formSelector);

        fetch(this.localeUrlPrefix + "/checkout/request-gift-card-balance",
            {
                body: JSON.stringify(data),
                headers: { "Content-Type": "application/json" },
                method: "POST",
            })
            .then(response => {
                if (!response || !response.ok) {
                    throw new Error("Request gift card balance failed.");
                }
                return response.json();
            })
            .then((data: IGiftCardValidation) => {
                window.FormSubmitToggle.EnableForm(this.formSelector);
                this.onValidateGiftCardSuccess(data);
            })
            .catch((error: Error) => {
                this.setGiftCardFieldState(false);
                window.IssueLogger.log(error.message, "onValidateGiftCard", error);
                this.onValidateGiftCardFail("Gift card could not be validated");
                window.FormSubmitToggle.EnableForm(this.formSelector);
            });
    }

    private setGiftCardFieldState(disabled: boolean): void {
        this.giftCardNumberElement.disabled = disabled;
        this.giftCardVerificationElement.disabled = disabled;
    }

    private setGiftCardValidationUi(areGiftCardFieldsValid: boolean, message: string): void {
        const validator = document.querySelector("span[data-valmsg-for='GiftCardNumber']");

        if (areGiftCardFieldsValid) {
            const addedValidator = document.querySelector("span[data-valmsg-for='GiftCardAdded']");

            validator.classList.remove("field-validation-error");
            validator.classList.add("field-validation-valid");
            addedValidator.classList.remove("field-validation-error");
            addedValidator.classList.add("field-validation-valid");
        }
        else {
            validator.classList.add("field-validation-error");
            validator.classList.remove("field-validation-valid");
            validator.textContent = message;
        }
    }

    /**
     * Validating the gift card fields can return a cached state unless we pass a callback to the validation function.
     * This is a bit of a flaw in the validation client lib we use. Therefore, we trigger the validation of the
     * two fields in a chain and when both are complete, check the result.
     *
     * With that result we call back to the client validation complete function so the server validation can then
     * take over. This means we've got 4 steps in the client validation which can be confusing.
     *
     * If we validated the whole form this would be simpler but we want to validate the gift card fields separately
     * so that on clicking 'add gift card' we don't get a credit card validation error.
     * @param e
     */
    private onValidateGiftCardRequested(e: MouseEvent): void {
        e.preventDefault();
        // Trigger validation for both fields.
        this.triggerGiftCardNumberValidation((isValid) => this.onGiftCardClientValidationComplete(isValid));
    }

    /** Step 1 of gift card validation. Checks just the gift card number and registers a callback for the result. */
    private triggerGiftCardNumberValidation(callback: GiftCardClientValidationCallback): void {
        window.validationService.isFieldValid(this.giftCardNumberElement, true, (isValid) => this.onGiftCardClientNumberValidated(isValid, callback));
    }

    /** Step 2 of gift card validation. Forwards the result of the gift card number validation and triggers validation on the verification number. */
    private onGiftCardClientNumberValidated(isValid: boolean, callback: GiftCardClientValidationCallback): void {
        this.triggerGiftCardVerifyValidation(isValid, callback);
    }

    /** Step 3 of gift card validation. Checks just the gift card verification number and registers a callback for the result. */
    private triggerGiftCardVerifyValidation(isGiftCardNumberValid: boolean, callback: GiftCardClientValidationCallback): void {
        window.validationService.isFieldValid(this.giftCardVerificationElement, true, (isValid) => this.onGiftCardClientVerifyValidated(isGiftCardNumberValid, isValid, callback));
    }

    /** Step 4 of gift card validation. Checks the result of both fields and passes the state to the final validation function. */
    private onGiftCardClientVerifyValidated(isGiftCardNumberValid: boolean, isGiftCardVerifyValid: boolean, callback: GiftCardClientValidationCallback): void {
        callback(isGiftCardNumberValid && isGiftCardVerifyValid);
    }

    private onValidateGiftCardFail(message: string): void { // When we can't even connect to the service.
        this.setGiftCardValidationUi(false, message);
    }

    private onValidateGiftCardSuccess(data: IGiftCardValidation): void {
        if (data.errorMessage !== '') {
            this.setGiftCardValidationUi(false, data.errorMessage);
            this.setGiftCardFieldState(false);
            return;
        }
        this.setGiftCardValidationUi(true, "");
        this.setGiftCardBalanceVisibility(true);
        // The form is re-enabled and so will be the gift card fields. Disable them again to prevent tampering.
        this.setGiftCardFieldState(true);

        this.removeGiftCardLink.classList.remove("hidden");

        this.processElements(document.body, ".c-gift-card-balance", (element) => element.textContent = data.balanceToPay);
        document.querySelector(".c-gift-card-amount").textContent = data.amountOnCard;
        document.querySelector(".c-gift-card-amount-negative").textContent = "-" + data.amountFromCard;

        // Rebind Apple Pay with the new total and line items that includes the gift card.
        let currencyIsoCode = this.readFieldValue("BasketTotals_CurrencyIsoCode");
        this.configureApplePay(this.checkout, data.totalInUnits, data.subTotal, data.delivery, data.amountFromCardNumeric, currencyIsoCode);

        // Mark the gift card as added so it can pass validation.
        this.giftCardAddedElement.value = "true";
        this.validateGiftCard.classList.add("hidden");

        if (!data.additionalPaymentRequired) {
            // Untick all the other payment methods as the gift card fully covers the balance.
            this.setCheckbox(this.paymentTypeApplePaySelected, false);
            this.setCheckbox(this.paymentTypeCardSelected, false);
            this.setCheckbox(this.paymentTypeGooglePaySelected, false);
            this.setCheckbox(this.paymentTypePayByBankSelected, false);
            this.setCheckbox(this.paymentTypePayPalSelected, false);
        }
    }

    private SetPaymentBlocksVisiblity(): void {
        let paymentTypes = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName("pay-option");
        let applePayVisible = false,
            cardVisible = false,
            giftCardVisible = false,
            googlePayVisible = false,
            payByBankVisible = false,
            payPalVisible = false;

        for (const element of paymentTypes) {
            let paymentType = element;

            if (paymentType.checked) {
                if (paymentType.id === this.paymentTypeApplePaySelected.id) {
                    applePayVisible = true;
                }
                else if (paymentType.id === this.paymentTypeCardSelected.id) {
                    cardVisible = true;
                }
                else if (this.paymentTypeGiftCardSelected && paymentType.id === this.paymentTypeGiftCardSelected.id) {
                    giftCardVisible = true;
                }
                else if (paymentType.id === this.paymentTypeGooglePaySelected.id) {
                    googlePayVisible = true;
                }
                else if (paymentType.id === this.paymentTypePayPalSelected.id) {
                    payPalVisible = true;
                }
                else if (paymentType.id === this.paymentTypePayByBankSelected.id) {
                    payByBankVisible = true;
                }
            }
        }

        this.setApplePayVisibility(applePayVisible);
        this.setCreditCardVisibility(cardVisible);
        this.setGiftCardEntryVisibility(giftCardVisible);
        this.setGooglePayVisibility(googlePayVisible);
        this.setPayByBankVisibility(payByBankVisible)
        this.setPayPalVisibility(payPalVisible);

        let continueCheckoutContainer = document.getElementById("ContinueCheckout");
        if (applePayVisible || googlePayVisible || payByBankVisible || payPalVisible) {
            continueCheckoutContainer.classList.add("hidden");
        }
        else {
            continueCheckoutContainer.classList.remove("hidden");
        }
    }

    private setApplePayVisibility(isVisible: boolean): void {
        this.setPaymentTickVisibility(isVisible, this.paymentTypeApplePaySelected, "applePay-outer-container");
    }

    private setCreditCardVisibility(isVisible: boolean): void {
        let paymentBlockCard = document.getElementById("card-component-form");

        if (isVisible) {
            paymentBlockCard.classList.remove("hidden");
        }
        else {
            paymentBlockCard.classList.add("hidden");
        }
    }

    private setGooglePayVisibility(isVisible: boolean): void {
        this.setPaymentTickVisibility(isVisible, this.paymentTypeGooglePaySelected, "googlePay-outer-container");
    }

    private setPayByBankVisibility(isVisible: boolean): void {
        this.setPaymentTickVisibility(isVisible, this.paymentTypePayByBankSelected, "payByBank-outer-container");
    }

    private setPayPalVisibility(isVisible: boolean): void {
        this.setPaymentTickVisibility(isVisible, this.paymentTypePayPalSelected, "paypal-outer-container");
    }

    private setPaymentTickVisibility(isVisible: boolean, tickElement: HTMLInputElement, outerContainerId: string): void {
        let paymentButtonContainer = document.getElementById(outerContainerId);

        if (isVisible) {
            paymentButtonContainer.classList.remove("hidden");
        }
        else {
            paymentButtonContainer.classList.add("hidden");
        }
    }

    private setGiftCardEntryVisibility(isVisible: boolean): void {
        const paymentBlockGiftCard = document.querySelector(".gift-card-details");

        if (!paymentBlockGiftCard || !this.paymentTypeGiftCardSelected) {
            // Gift card payment is not available and the UI elements are missing.
            return;
        }

        if (isVisible) {
            paymentBlockGiftCard.classList.remove("hidden");

            let giftCardBalance = document.querySelector(".c-gift-card-amount").innerHTML;
            if (giftCardBalance) {
                this.setGiftCardBalanceVisibility(true);
            }
            else {
                this.setGiftCardBalanceVisibility(false);
                // Gift card may have been hidden by 'remove' link.
                this.validateGiftCard.classList.remove("hidden");
            }

        } else {
            paymentBlockGiftCard.classList.add("hidden");
            this.removeGiftCardLink.classList.add("hidden");
            this.setGiftCardBalanceVisibility(false);
            this.removeGiftCard();

            this.giftCardNumberElement.value = "";
            this.giftCardVerificationElement.value = "";
            document.querySelector(".c-gift-card-amount").textContent = "";
            this.processElements(document.body, ".c-gift-card-balance", (element) => element.textContent = "");
        }
    }

    private setGiftCardBalanceVisibility(show: boolean): void {
        if (show) {
            this.processElements(document.body, ".c-gift-card-balance-container", (element) => element.classList.remove("hidden"));
        }
        else {
            this.processElements(document.body, ".c-gift-card-balance-container", (element) => element.classList.add("hidden"));
        }
    }

    private delayedCardholderCheck(ev: Event): void {
        // onCut / onPaste events fire before data is in the control, delay the check to test if data is still there.
        setTimeout(() => this.onCardholderNameChanged(ev), 10);
    }

    private onCardholderNameChanged(ev: Event): void {
        this.validateCardholderName();
    }

    private loadAdyenConfiguration(): CoreConfiguration {
        let paymentMethods = JSON.parse(this.readFieldValue("AdyenConfiguration_PaymentMethods"));

        let adyenConfiguration: CoreConfiguration = {
            clientKey: this.readFieldValue("AdyenConfiguration_ClientKey"),
            countryCode: this.readFieldValue("AdyenConfiguration_CountryCode2"),
            environment: this.readFieldValue("AdyenConfiguration_Environment") as AdyenEnvironment,
            // If we want to localise Adyen then pass in a supported locale here. 
            // The rest of the UI is in English so probably not worth doing. 
            // https://docs.adyen.com/checkout/components-web/localization-components.
            locale: "en-US",
            // onAdditionalDetails must be registered at the checkout level, not the payment method.
            onAdditionalDetails: (state, component, actions) => { this.handleOnAdditionalDetails(state, component, actions); },
            onError: (error) => {
                window.IssueLogger.log("Adyen top level error - ", "Adyen", error);
            },
            paymentMethodsResponse: paymentMethods,
        };

        return adyenConfiguration;
    }

    private readFieldBool(elementId): boolean {
        let element = <HTMLInputElement>document.getElementById(elementId);
        return element.value.toLowerCase() == "true";
    }

    private readFieldValue(fieldId: string): string {
        let element = <HTMLInputElement>document.getElementById(fieldId);

        if (!element) {
            element = <HTMLInputElement>document.getElementsByName(fieldId)[0];
        }

        if (element) {
            return element.value;
        }

        return "";
    }

    private showPaymentFailedModal(errorMessage: string): void {
        let errorMessageElement = <HTMLElement>document.getElementById("failed-payment-message");
        errorMessageElement.innerHTML = errorMessage;

        let errorModal = <HTMLDivElement>document.getElementById("failed-payment-modal");
        window.Modal.OpenModal(errorModal, () => window.FormSubmitToggle.EnableForm(this.formSelector));

        //this.turnstileWrapper.Reset();

        // Scroll to the top so the failure can be seen, otherwise it might stay out of sight on thin displays.
        window.scrollTo(0, 0);
    }

    public handleOnApplePayDetailsChange(state: OnChangeData, component): void {
        if (state.isValid) {
            this.applePayComponentData = state.data;
        }
    }

    public handleOnGooglePayDetailsChange(state: OnChangeData, component): void {
        if (state.isValid) {
            this.googlePayComponentData = state.data;
        }
    }

    public handleOnPayByBankDetailsChange(state: OnChangeData, component): void {
        if (state.isValid) {
            this.payByBankData = state.data;
        }
    }

    private updateAdyenValidationErrors(errors: ValidationError[]) {
        for (const error of errors) {
            this.updateAdyenValidationError(error);
        }
    }

    /**
     * 
     * @param state
     * @param component
     * @param actions - Can be null if the payment is frictionless. This is passed in from Adyen.
     */
    public handleOnAdditionalDetails(state: AdditionalDetailsData, component, actions?): void {
        let postData = {
            idempotencyKey: this.idempotencyKey,
            threeDSResult: state.data.details.threeDSResult,
            paymentData: component.props.paymentData,
        };
        let jsonPost = JSON.stringify(postData);
        window.HttpRequest.PostJson('/checkout/validate-3ds2', jsonPost,
            (jsonResponse) => { this.on3ds2ResponseReceived(jsonResponse, actions); },
            (jsonResponse) => {
                this.postError("handleOnAdditionalDetails", jsonResponse); actions?.reject()
            });
    }

    private on3ds2ResponseReceived(json: string, actions?): void {
        let initiateResponse = <I3ds2Response>JSON.parse(json);
        let paymentsResponse = initiateResponse.adyenResponse;

        if (paymentsResponse) {
            // 3ds2 payment flow finished, present the result.
            if (initiateResponse.mainRedirectUrl) {
                if (actions) { // Some 3ds2 cards are 'frictionless' and do not require user interaction, plus they won't have actions.
                    const { resultCode, action, order } = initiateResponse.clientData;
                    actions.resolve({ resultCode, action, order });
                }
                window.location.href = initiateResponse.mainRedirectUrl;
            }
            else if (paymentsResponse.resultCode === "Refused") {
                this.showPaymentFailedModal("Your bank has declined payment.<br />");
                this.idempotencyKey = initiateResponse.idempotencyKey;
                actions?.reject();
                //this.turnstileWrapper.Reset();
            }
            else {
                this.showPaymentFailedModal("We received an error when processing your payment.");
                // This should have been logged on the server. Logging it here as well would tell us if it was a real client error or someone fiddling with the API.
                window.IssueLogger.log("3DS2 failed", "On3DS2ResultCode", paymentsResponse.resultCode);
                this.idempotencyKey = initiateResponse.idempotencyKey;
                //this.turnstileWrapper.Reset();
                actions?.reject();
            }
        }
        else {
            if (initiateResponse.mainRedirectUrl) {
                // If the server does not have a cart (due to order already being paid) then it will redirect us.
                window.location.href = initiateResponse.mainRedirectUrl;
                actions?.resolve();
                return;
            }
            // This will happen with network issues connecting to the payment provider and is logged on the server.
            this.showPaymentFailedModal("Sorry, we could not process your payment.");
            this.idempotencyKey = initiateResponse.idempotencyKey;
            //this.turnstileWrapper.Reset();
            actions?.reject();
        }
    }

    private validateCardholderName(): void {
        if (this.enableValidationForCardholderNameField) {
            const cardholderName = this.cardholderNameElement.value;
            const cardholderNameIsValid = (cardholderName.length !== 0 && !!cardholderName.trim());

            this.setCardInputCss(this.cardholderNameElement, cardholderNameIsValid);
        }

        if (this.enableValidationForMainCardFields) {
            this.cardComponent.showValidation();
        }

        this.enableValidationForMainCardFields = false;
    }

    /**
     * Sets the passed card input field to show a green underline if the contents of the field are valid.
     * @param element - Element to show underline beneath. Required, cannot be null / undefined.
     * @param valid   - True to show the underline, otherwise false.
     */
    private setCardInputCss(element: HTMLElement, valid: boolean): void {
        if (valid) {
            if (!element.classList.contains(this.validFieldCss)) {
                element.classList.add(this.validFieldCss);
            }
        }
        else if (element.classList.contains(this.validFieldCss)) {
            element.classList.remove(this.validFieldCss);
        }
    }

    private catchSubmitOnPaymentForm(): void {

        // We don't get a submit event if the form fails validation. As some of the fields
        // are managed by Adyen we need to trigger the validation there as well when the
        // main validation runs. The following listens for the form validation event and
        // calls the Adyen validation if required.
        this.form.addEventListener("validation", (e) => {
            this.validateAdyen();
        });

        this.form.addEventListener("submit", (e) => this.onFormSubmit(e));
    }

    private onFormSubmit(e: Event): void {
        e.preventDefault();

        // Form submitted by user so we should not prevent validation of the fields.
        this.enableValidationForCardholderNameField = true;
        this.enableValidationForMainCardFields = true;

        let allFieldsValid = true;

        if (!window.validationService.isValid(this.form)) {
            allFieldsValid = false;
        }

        if (!this.validateAdyen()) {
            allFieldsValid = false;
        }

        if (allFieldsValid) {
            this.initiatePayment();
            window.FormSubmitToggle.DisableForm(this.formSelector);
        }
    }

    private validateAdyen(): boolean {
        if (this.paymentTypeCardSelected.checked) {
            if (!this.adyenCardFieldsValid()) {
                // Trigger the Adyen card component validation which will show validation errors and call back to our cardholder name validation event.
                this.cardComponent.showValidation();
                return false;
            }
        }

        return true;
    }

    public adyenCardFieldsValid(): boolean {
        return this.cardComponent.isValid;
    }

    private formAsJson(): string {
        let formModel: IPayment = {
            address: {
                company: this.readFieldValue("Address_Company"),
                countryCodeIso3: this.readFieldValue("Address_CountryCodeIso3"),
                county: this.readFieldValue("Address_County"),
                district: this.readFieldValue("Address_District"),
                editorVisible: this.IsAddressEditorVisible(),
                line1: this.readFieldValue("Address_Line1"),
                line2: this.readFieldValue("Address_Line2"),
                postalCode: this.readFieldValue("Address_PostalCode"),
                stateRequired: this.readFieldBool("Address_StateRequired"),
                town: this.readFieldValue("Address_Town"),
            },
            applePayData: this.applePayComponentData,
            cardData: this.cardComponent.data,
            cardHolderName: this.cardholderNameElement.value,
            //cloudflareTurnstileResponse: this.turnstileWrapper.ReadToken(),
            giftCardNumber: this.readFieldValue("GiftCardNumber"),
            giftCardVerificationCode: this.readFieldValue("GiftCardVerificationCode"),
            googlePayData: this.googlePayComponentData,
            idempotencyKey: this.idempotencyKey,
            isBillingAddressSameAsDelivery: this.isBillingSameAsDelivery(),
            marketingRequest: this.marketingFields(),
            paymentTypeApplePaySelected: this.paymentTypeApplePaySelected.checked,
            paymentTypeCardSelected: this.paymentTypeCardSelected.checked,
            paymentTypeGiftCardSelected: this.paymentTypeGiftCardSelected?.checked ?? false,
            paymentTypeGooglePaySelected: this.paymentTypeGooglePaySelected.checked,
            paymentTypePayByBankSelected: this.paymentTypePayByBankSelected.checked,
            paymentTypePayPalSelected: this.paymentTypePayPalSelected.checked,
            payByBankData: this.payByBankData,
        }

        return JSON.stringify(formModel);
    }

    private marketingFields(): IMarketingRequest {
        return {
            requestEmail: this.marketingField("MarketingRequest.RequestEmail"),
            requestPost: this.marketingField("MarketingRequest.RequestPost"),
            requestThirdParty: this.marketingField("MarketingRequest.RequestThirdParty"),
        };
    }

    private marketingField(fieldName: string): PreferenceAnswer {
        let fields = <NodeListOf<HTMLInputElement>>document.getElementsByName(fieldName);

        // If one of the radio buttons is checked, return the value of that (which will be a PreferenceAnswer).
        if (fields.length === 2) {
            for (var i = 0; i < fields.length; i++) {
                let field = fields[i];

                if (field.checked) {
                    return <PreferenceAnswer>field.value;
                }
            }
        }
        else if (fields.length === 1) {
            // Hidden field with the previously answered value.
            return <PreferenceAnswer>fields[0].value;
        }

        // No radio buttons found or none checked, return as not answered.
        return PreferenceAnswer.NotAnswered;
    }

    private initiatePayment(actions?): void {
        let json = this.formAsJson();

        window.HttpRequest.PostJson('/checkout/initiate-payment', json,
            (data) => { this.onPaymentDetailsReceived(data, actions); },
            (data) => {
                actions?.reject(); this.postError("initiatePayment", data)
            },
        );
    }

    private postError(context: string, data: any) {
        this.showPaymentFailedModal("Sorry, we could not contact your bank. This may only be temporary.");
        window.IssueLogger.log(`$Error - ${context}`, "PostError", data);
    }

    /**
     * 
     * @param json
     * @param actions : Might not be passed if the call is outside of Adyen (gift card or PayPal).
     */
    private onPaymentDetailsReceived(json, actions?) {
        let initiateResponse = <IInitiatePaymentResponse>JSON.parse(json);

        this.idempotencyKey = initiateResponse.idempotencyKey;
        if (initiateResponse.mainRedirectUrl) {
            if (actions && initiateResponse.clientData) {
                const { resultCode, action, order } = initiateResponse.clientData;
                actions.resolve({ resultCode, action, order });
            }
            window.location.href = initiateResponse.mainRedirectUrl;
        }
        else if (initiateResponse.resultCode == "IdentifyShopper" ||
            initiateResponse.resultCode == "ChallengeShopper") {
            const { resultCode, action } = initiateResponse.clientData;
            if (actions) {
                actions.resolve({ resultCode });
            }
            this.showThreeDs2Challenge(action);
        }
        else if (initiateResponse.resultCode == "Refused") {
            actions?.reject();
            this.showPaymentFailedModal("Your bank has declined payment.");
        }
        else {
            actions?.reject();
            let errMessage: string;
            //if (initiateResponse.turnstileSuccess === false) {
            //    errMessage = "The payment security check has failed to run, please check your browser is not blocking scripts from Cloudflare and refresh this page.";
            //}
            //else {
            // We should not get here as it means we have data we do not expect.
            window.IssueLogger.log("Unexpected result code", "UnexpectedResultCode", initiateResponse.resultCode);
            errMessage = "We received an error when processing your payment.";
            //}
            this.showPaymentFailedModal(errMessage);
        }
    }

    private showThreeDs2Challenge(actionJson: string): void {
        if (this.verificationComponent) {
            this.verificationComponent.unmount();
        }

        const threeDSConfiguration = {
            challengeWindowSize: "05"
            // Set to any of the following:
            // "02": ["390px", "400px"] -  The default window size
            // "01": ["250px", "400px"]
            // "03": ["500px", "600px"]
            // "04": ["600px", "400px"]
            // "05": ["100%", "100%"]
        }

        let action = JSON.parse(actionJson);
        this.verificationComponent = this.checkout.createFromAction(action, threeDSConfiguration);
        // When mounted this will call back to handleOnAdditionalDetails with the 3ds2 challenge result.
        this.verificationComponent.mount("#" + this.verificationContainerId);

        // Show the 3ds2 UI in a modal display.
        let modalDivElement = <HTMLDivElement>document.getElementById("verification-3ds2-modal");
        window.Modal.OpenModal(modalDivElement, () => {
            // Create a new key as the previous one is now used and will generate an error on an attempted re-use.
            this.idempotencyKey = Date.now().toString();
            document.getElementById(this.verificationContainerId).innerHTML = "";
            this.verificationComponent.unmount();
            this.verificationComponent = null;
            window.FormSubmitToggle.EnableForm(this.formSelector);
        });
    }

    private configureCreditCards(checkout): CustomCard {
        // STYLING, PLACEHOLDERS & ARIA LABELS FOR SECURED FIELDS
        // https://docs.adyen.com/developers/checkout/api-integration/configure-secured-fields/styling-secured-fields
        const styles = {
            base: {
                color: '#000',
                fontSize: '14px',
                lineHeight: '14px'
            },
            error: {
                color: '#cc0404'
            },
            placeholder: {
                color: '#d8d8d8'
            }
        };

        return new CustomCard(checkout, {
            placeholders: {
                cardNumber: "1234 5678 9012 3456",
                expiryDate: "MM/YY",
                securityCodeThreeDigits: "3 digits",
                securityCodeFourDigits: "4 digits",
            },
            styles,
            onBrand: (pCallbackObj) => this.onBrand(pCallbackObj),
            onConfigSuccess: (e) => this.onCustomCardConfigured(e),
            onFocus: (e) => this.onCustomCardFocus(e),
            onFieldValid: (cbObj) => this.onFieldValid(cbObj),
            onValidationError: (validationErrors => this.updateAdyenValidationErrors(validationErrors)),
        })
            .mount(".secured-fields");
    }

    private onCustomCardConfigured(e: CardConfigSuccessData): void {
        if (this.securedFieldsDiv) {
            this.securedFieldsDiv.style.display = 'block';
        }
    }

    private onCustomCardFocus(cardFocusData: CardFocusData): void {
        const sfNode = <HTMLElement>cardFocusData.rootNode.querySelector(`[data-cse="${cardFocusData.fieldType}"]`);

        // Add focus
        if (cardFocusData.focus) {
            sfNode.classList.add(this.focusCss);
            return;
        }
        sfNode.classList.remove(this.focusCss);
    }

    private onBrand(cbObj): void {
        if (this.securedFieldsDiv) {

            let cardBrandImg = this.securedFieldsDiv.querySelector('#pmImage');
            if (cardBrandImg) {
                cardBrandImg.setAttribute('src', cbObj.brandImageUrl);
            }
        }
        if (cbObj.hideCVC && !this.cvcHidden) {
            this.cvcHidden = true;
            let labelNodes = cbObj.rootNode.getElementsByClassName('pm-form-label--cvc');
            if (labelNodes.length > 0) {
                labelNodes[0].style.display = 'none';
            }
        }
        if (this.cvcHidden && cbObj.hideCVC === false) {
            this.cvcHidden = false;
            let labelNodes = cbObj.rootNode.getElementsByClassName('pm-form-label--cvc');
            if (labelNodes.length > 0) {
                labelNodes[0].style.display = 'block';
            }
        }
    }

    private updateAdyenValidationError(error: ValidationError) {
        if (error) {
            const sfNode = error.rootNode.querySelector(`[data-cse="${error.fieldType}"]`) as HTMLElement;

            if (sfNode) {
                const errorNode = <HTMLInputElement>sfNode.parentNode.querySelector('.pm-form-label__error-text');

                if (error.errorI18n) {
                    errorNode.style.display = 'block';
                    errorNode.innerText = error.errorI18n;

                    // Add error classes.
                    sfNode.classList.add("pm-input-field--error");
                }
                else {
                    errorNode.style.display = 'none';
                    errorNode.innerText = '';
                    sfNode.classList.remove("pm-input-field--error");
                }
            }
        }
    }

    private onFieldValid(cbObj: CardFieldValidData) {
        const sfNode = cbObj.rootNode.querySelector(`[data-cse="${cbObj.fieldType}"]`) as HTMLElement;

        if (sfNode) {
            const errorNode = sfNode.parentNode.querySelector('.pm-form-label__error-text') as HTMLInputElement;

            this.setCardInputCss(sfNode, cbObj.valid);

            if (errorNode) {
                errorNode.style.display = 'none';
                errorNode.innerText = '';

                // Remove error classes.
                sfNode.classList.remove("pm-input-field--error");
            }

            //this.validateCardholderName();
        }
    }

    private resetApplePay(checkout: any): void {
        if (checkout) {
            const basketAmounts = this.readBasketAmounts();
            this.configureApplePay(checkout, basketAmounts.total, basketAmounts.subTotal, basketAmounts.deliveryTotal, 0, basketAmounts.currencyIsoCode);
        }
    }

    private readBasketAmounts(): BasketAmounts {
        const currencyIsoCode = this.readFieldValue("BasketTotals_CurrencyIsoCode");
        const total = parseInt(this.readFieldValue("BasketTotals_TotalInUnits"));
        const subTotal = this.readFieldValue("BasketTotals_SubTotalAsDecimal");
        const deliveryTotal = this.readFieldValue("BasketTotals_DeliveryAsDecimal");
        const totalAsDecimal = this.readFieldValue("BasketTotals_TotalAsDecimal");

        return new BasketAmounts(currencyIsoCode, total, subTotal, deliveryTotal, totalAsDecimal);
    }

    private configureApplePay(checkout, total: number, subTotal: string, delivery: string, amountFromGiftCard: number, currencyIsoCode: string): void {
        let applePayContainer = document.getElementById("applepay-button-container");

        if (!applePayContainer) {
            // Apple Pay UI does not exist, possibly not enabled for this locale / site or a single use voucher has been added to the basket.
            return;
        }

        const lineItems = this.generateApplePayLineItems(subTotal, delivery, amountFromGiftCard);

        const applepay = new ApplePay(checkout, {
            amount: {
                currency: currencyIsoCode,
                value: total
            },
            buttonColor: "black",
            buttonType: "plain",

            totalPriceLabel: this.readFieldValue("AdyenConfiguration_MerchantName"),
            lineItems: lineItems,

            onAdditionalDetails: (state, component, actions) => actions.resolve({ resultCode: 'Authorised' }),

            onAuthorized: ({ authorizedEvent, billingAddress, deliveryAddress }, actions) => {
                // Called just before onSubmit. When using an Apple Pay Express the addresses can
                // be checked here and rejected if they are not valid. This script is only used on
                // the final payment page where Express is not used so these are null here.
                actions.resolve();
            },
            onChange: (state, component) => { this.handleOnApplePayDetailsChange(state, component); },
            // Called when the shopper clicks the Apple Pay button. Call resolve() or reject() to continue or stop the payment flow.
            // This can be used to validate the form and then proceed if we need to.
            onClick: (
                resolve: (result?: ApplePayJS.ApplePayPaymentAuthorizationResult) => void,
                reject: (result?: ApplePayJS.ApplePayPaymentAuthorizationResult) => void) => this.onPayWithThirdPartyClicked(resolve, reject),
            onSubmit: (state, component, actions) => this.onApplePaySubmit(state, component, actions),
        });

        applepay
            .isAvailable()
            .then(() => {
                // Show the '--or--' divider that separates the payment buttons.
                document.querySelectorAll(".or-hr").forEach(element => {
                    element.classList.remove("hidden");
                });
                applepay.mount("#applepay-button-container");

                let tickSelector = document.getElementById("payment-select-applepay");

                tickSelector.classList.remove("hidden");
            })
            .catch((error) => {
                if (error.message.indexOf("is not available on this device") < 0) {
                    window.IssueLogger.log("Apple Pay (full) failed to mount", "ApplePay", error.toString());
                }
            });
    }

    private generateApplePayLineItems(subTotal: string, delivery: string, amountFromGiftCard: number) {
        let lineItems = [];

        lineItems.push({ label: "Items", "amount": subTotal });
        lineItems.push({ label: "Delivery", "amount": delivery });

        if (amountFromGiftCard) {
            lineItems.push({ label: "Gift card", "amount": "-" + amountFromGiftCard.toFixed(2).toString() });
        }

        return lineItems;
    }

    public onApplePaySubmit(state: SubmitData, component, actions): void {
        if (state.isValid) {
            this.initiatePayment(actions);
            window.FormSubmitToggle.DisableForm(this.formSelector);
        }
    }

    private configurePayByBank(checkout: any): void {
        const openBanking = new PayByBank(checkout, {
            // Called when the shopper clicks the Pay by bank button. Call resolve() or reject() to continue or stop the payment flow.
            // This can be used to validate the form and then proceed if we need to.
            // TODO: Not in new version, maybe onChange will suffice.
            //onClick: (resolve, reject) => this.onPayWithThirdPartyClicked(resolve, reject),

            onError: (error) => {
                window.IssueLogger.log("Adyen Pay by bank error - ", "PayByBank", error);
            },

            // Called before onSubmit, same arguments.
            onChange: (state, component) => { this.handleOnPayByBankDetailsChange(state, component); },

            // Called after the shopper approves the payment. The first parameter(state) of this function
            // contains all the necessary data to make a payment using the /payments API.
            onSubmit: (state, component, actions) => this.onPayByBankSubmit(state, component, actions),
        });

        openBanking
            .isAvailable()
            .then(() => {
                openBanking.mount("#payByBank-button-container");

                let tickSelector = document.getElementById("payment-select-paybybank");

                tickSelector.classList.remove("hidden");
            })
            .catch(error => {
                if (error.message.indexOf("is not available on this device") < 0) {
                    window.IssueLogger.log("Pay by bank failed to mount", "PayByBank", error.toString());
                }
            });
    }

    private generateGoogleLineItems(subTotal: string, delivery: string, amountFromGiftCard: number) {
        let lineItems = [];

        lineItems.push({ label: "Items", type: "LINE_ITEM", price: subTotal });
        lineItems.push({ label: "Delivery", type: "LINE_ITEM", price: delivery });

        if (amountFromGiftCard) {
            lineItems.push({ label: "Gift card", type: "LINE_ITEM", price: "-" + amountFromGiftCard.toFixed(2).toString() });
        }

        return lineItems;
    }

    private configureGooglePay(checkout: any): void {
        const basketAmounts = this.readBasketAmounts();
        const lineItems = this.generateGoogleLineItems(basketAmounts.subTotal, basketAmounts.deliveryTotal, 0);

        const googlepay = new GooglePay(checkout, {

            amount: {
                currency: basketAmounts.currencyIsoCode,
                value: basketAmounts.total
            },

            environment: this.readFieldValue("AdyenConfiguration_GoogleEnvironment"),
            buttonColor: "black",
            buttonSizeMode: "fill",

            countryCode: this.readFieldValue("AdyenConfiguration_CountryCode2"),

            configuration: {
                gatewayMerchantId: this.readFieldValue("AdyenConfiguration_MerchantAccount"),
                merchantId: this.readFieldValue("AdyenConfiguration_GoogleMerchantId"),
                merchantName: this.readFieldValue("AdyenConfiguration_MerchantName"),
            },

            transactionInfo: {
                countryCode: this.readFieldValue("AdyenConfiguration_CountryCode2"),
                currencyCode: basketAmounts.currencyIsoCode,
                displayItems: lineItems,
                totalPrice: basketAmounts.totalAsDecimal,
                totalPriceLabel: "Total",
                totalPriceStatus: "FINAL"
            },

            // Called when the shopper clicks the Google Pay button. Call resolve() or reject() to continue or stop the payment flow.
            // This can be used to validate the form and then proceed if we need to.
            onClick: (resolve, reject) => this.onPayWithThirdPartyClicked(resolve, reject),

            // Called before onSubmit, same arguments.
            onChange: (state, component) => { this.handleOnGooglePayDetailsChange(state, component); },

            // Called after the shopper approves the payment on the Google Pay form. The first parameter(state) of this function
            // contains all the necessary data to make a payment using the /payments API.
            onSubmit: (state, component, actions) => this.onGooglePaySubmit(state, component, actions),
        });

        googlepay
            .isAvailable()
            .then(() => {
                googlepay.mount("#googlepay-button-container");

                let tickSelector = document.getElementById("payment-select-googlepay");

                tickSelector.classList.remove("hidden");
            })
            .catch(error => {
                if (error.message.indexOf("is not available on this device") < 0) {
                    window.IssueLogger.log("Google Pay failed to mount", "GooglePay", error.toString());
                }
            });
    }

    public onGooglePaySubmit(state: SubmitData, component, actions): void {
        if (state.isValid) {
            this.googlePayComponentData = state.data;
            this.initiatePayment(actions);
            window.FormSubmitToggle.DisableForm(this.formSelector);
        }
    }

    public onPayByBankSubmit(state, component, actions): void {
        if (state.isValid) {
            this.payByBankData = state.data;
            this.initiatePayment(actions);
            window.FormSubmitToggle.DisableForm(this.formSelector);
        }
    }

    private MapLoqateFields(): void {
        let options = {
            key: "TM62-NA98-RK41-AU82",
            suppressAutocomplete: false,
        };
        let fields = [
            { element: "search", field: "" },
            { element: "Address_Company", field: "Company", mode: pca.fieldMode.DEFAULT },
            { element: "Address_Line1", field: "Line1" },
            { element: "Address_Line2", field: "Line2", mode: pca.fieldMode.POPULATE },
            { element: "Address_District", field: "Line3", mode: pca.fieldMode.POPULATE },
            { element: "Address_Town", field: "City", mode: pca.fieldMode.POPULATE },
            { element: "Address_County", field: "Province", mode: pca.fieldMode.POPULATE },
            { element: "Address_PostalCode", field: "PostalCode" },
            { element: "Address_CountryCodeIso3", field: "CountryName", mode: pca.fieldMode.COUNTRY }
        ];

        let control = new pca.Address(fields, options);
        control.listen("populate", (address, variations) => this.loquatePopulated(address, variations));
    }

    private loquatePopulated(address, variations): void {
        this.setFieldLabelsByCountryCode(address.CountryIso3);
    }

    private onCountryChanged(): void {
        let countryOption = this.selectedCountryOption();

        if (countryOption) {
            this.setFieldLabelsByCountryCode(countryOption.value);
        }
    }

    private selectedCountryOption(): HTMLOptionElement {
        let countryDropdown = <HTMLSelectElement>document.getElementById("Address_CountryCodeIso3");

        if (countryDropdown && countryDropdown.selectedIndex >= 0) {
            return countryDropdown.options[countryDropdown.selectedIndex];
        }

        return null;
    }

    private setFieldLabelsByCountryCode(countryIso3: string): void {
        let countyElement = <HTMLInputElement>document.querySelector("label[for=Address_County]");
        let stateRequirementElement = <HTMLInputElement>document.getElementById("Address_StateRequired");

        if (countryIso3 === "USA" || countryIso3 === "CAN") {
            countyElement.innerText = "State:";
            stateRequirementElement.value = "true";
        }
        else {
            countyElement.innerText = "County:";
            stateRequirementElement.value = "false";
        }
    }
}

if (document.body.dataset.pageInit == "payment") {
    Payment.InitAsync();
}
